import type Owner from '@ember/owner';
import { setOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import type { IntlService } from 'ember-intl';
import { OTB_DEFAULT_VALUE } from 'frontend-utils/utils/constants/constant-values';

export class OutOfOrderRooms {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  /*
    Determines whether the PMS has out of order rooms data, specified by a feature flag.
    For Rates, Market and Revenue Insight SP: check the pms.out_of_order FF.
    For Revenue Insight MP: we currently always support out of order rooms (all integrations support OOO there).
  */
  get inventoryText(): string {
    if (this.isSupported) {
      return this.intl.t('rates.booking_curve.inventory_excl_ooo');
    }

    return this.intl.t('rates.booking_curve.inventory');
  }

  get isSupported(): boolean {
    if (this.hotelAndMarketService.hotel) {
      return this.access.hasFeature('pms.out_of_order');
    }

    return true;
  }

  constructor(owner: Owner) {
    setOwner(this, owner);
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function capOccupancyRate(occupancyRate: number | null) {
  if (occupancyRate === null || occupancyRate < 1) {
    return {
      value: occupancyRate,
      isCapped: false,
    };
  }

  return {
    value: 1,
    isCapped: true,
  };
}

export function getOccupancyRatio(
  roomsBooked: number | undefined,
  totalCapacity: number,
): number | null {
  if (roomsBooked === undefined || hasOTBDefaultValue(roomsBooked)) {
    return null;
  }

  if (totalCapacity === 0) {
    return null;
  }

  return roomsBooked / totalCapacity;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getPredictedOccupancyRate(
  roomsBooked: number | undefined,
  totalCapacity: number,
) {
  const occupancyRate = getOccupancyRatio(roomsBooked, totalCapacity);

  return capOccupancyRate(occupancyRate).value;
}

export function hasOTBDefaultValue(value: number): boolean {
  return value === OTB_DEFAULT_VALUE;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function isPredictedOccupancyRateCapped(
  roomsBooked: number | undefined,
  totalCapacity: number,
) {
  const occupancyRate = getOccupancyRatio(roomsBooked, totalCapacity);

  return capOccupancyRate(occupancyRate).isCapped;
}

export function formatPercentage(
  intl: IntlService,
  value: number | null | undefined,
): string | undefined {
  if (value === null || value === undefined) {
    return;
  }

  return intl.formatNumber(value, {
    style: 'percent',
  });
}
